import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../hooks/useToast';
import type { ApiError } from '../../../lib/api';
import { TenantApiClient } from '../../../lib/tenant-api';
import {
  createSegmentDefinitionFromTemplate,
  fetchAvailableFields,
  fetchAvailableFieldsWithTypes,
  getSegmentDefinition,
  previewSegmentRecipients,
  updateSegmentDefinition,
  type CreateSegmentDefinitionParams,
  type UpdateSegmentDefinitionParams,
} from './segment-definitions-api';

export function useAvailableFields() {
  return useQuery({
    queryKey: ['availableSegmentFields'],
    queryFn: fetchAvailableFields,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useAvailableFieldsWithTypes() {
  return useQuery({
    queryKey: ['availableSegmentFieldsWithTypes'],
    queryFn: fetchAvailableFieldsWithTypes,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

interface UseCreateSegmentFromTemplateOptions {
  onSuccess?: (segmentId: string) => void;
}

interface CreateSegmentParams {
  name: string;
  description: string;
  contents?: CreateSegmentDefinitionParams['contents'];
  conditionGroups?: CreateSegmentDefinitionParams['conditionGroups'];
  targetedTenantsFilter?: CreateSegmentDefinitionParams['targetedTenantsFilter'];
}

export function useCreateSegmentFromTemplate(options?: UseCreateSegmentFromTemplateOptions) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const mutationFn = (params: CreateSegmentParams) => {
    return createSegmentDefinitionFromTemplate({
      name: params.name,
      description: params.description,
      contents: params.contents,
      conditionGroups: params.conditionGroups,
      targetedTenantsFilter: params.targetedTenantsFilter,
    });
  };

  const onMutate = (params: CreateSegmentParams) => {
    if (!params.name.trim() || !params.description.trim()) {
      const validationError = new Error('Segment name and description required');
      addToast({
        title: 'Segment name and description required',
        description: 'Please enter a segment name and description.',
        variant: 'destructive',
      });
      throw validationError;
    }
  };

  const onSuccess = (result: { canonicalVersionId?: string } | null) => {
    queryClient.invalidateQueries({
      queryKey: ['segmentDefinitions'],
    });

    addToast({
      title: 'Segment created successfully',
      description: 'Segment has been created.',
      variant: 'default',
    });

    if (result?.canonicalVersionId && options?.onSuccess) {
      options.onSuccess(result.canonicalVersionId);
    }
  };

  const onError = (error: Error) => {
    // Validation errors already show a toast in onMutate
    if (error.message !== 'Segment name and description required') {
      // Extract detailed message from API error response if available
      const apiError = error as ApiError;
      const apiMessage = apiError.response?.data?.message || apiError.response?.data?.error;

      addToast({
        title: 'Error creating segment',
        description: apiMessage || error.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return useMutation({
    mutationFn,
    onMutate,
    onSuccess,
    onError,
  });
}

export function useSegmentDefinition(canonicalVersionId: string | undefined) {
  return useQuery({
    queryKey: ['segmentDefinition', canonicalVersionId],
    queryFn: () => getSegmentDefinition(canonicalVersionId!),
    enabled: !!canonicalVersionId,
  });
}

interface UseUpdateSegmentOptions {
  onSuccess?: () => void;
}

export function useUpdateSegment(canonicalVersionId: string, options?: UseUpdateSegmentOptions) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (params: UpdateSegmentDefinitionParams) => {
      return updateSegmentDefinition(canonicalVersionId, params);
    },
    onSuccess: () => {
      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: ['segmentDefinitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['segmentDefinition', canonicalVersionId],
      });

      addToast({
        title: 'Segment updated successfully',
        description: 'Your changes have been saved.',
        variant: 'default',
      });

      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      const apiError = error as ApiError;
      const apiMessage = apiError.response?.data?.message || apiError.response?.data?.error;

      addToast({
        title: 'Error updating segment',
        description: apiMessage || error.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export { useTenants } from '../../../components/tenant-selector/useTenants';

export function usePreviewSegmentRecipients() {
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (params: {
      tenantId: string;
      conditions?: BusinessSegmentConditions;
      conditionGroups?: SegmentConditionGroups;
      limit?: number;
      cursor?: string;
    }) => {
      // Support both legacy (conditions) and new (conditionGroups) formats
      return previewSegmentRecipients(
        new TenantApiClient(params.tenantId),
        params.conditions,
        params.conditionGroups,
        params.limit,
        params.cursor,
      );
    },
    onError: (error: Error) => {
      const apiError = error as ApiError;
      const apiMessage = apiError.response?.data?.message || apiError.response?.data?.error;

      addToast({
        title: 'Error previewing recipients',
        description: apiMessage || error.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}
